/**
 * Filter vehicles by ALL active filters (AND between groups, OR within).
 * Operates on the byVehicle map from ice-pack data.
 *
 * @param {object} byVehicle - Vehicles keyed by globalVehicleId
 * @param {object} activeFilters - Current filter selections
 * @returns {Array} Filtered vehicle entries
 */
export const filterVehicles = (byVehicle, activeFilters) => {
	if (!byVehicle) return [];

	const vehicleEntries = Object.values(byVehicle);

	return vehicleEntries.filter((vehicle) => {
		return Object.entries(activeFilters).every(
			([facetKey, selectedValues]) => {
				if (!selectedValues || selectedValues.length === 0) return true;

				if (facetKey === 'offerTypes') {
					return selectedValues.some((slug) =>
						vehicle.offerTypes.some(
							(type) => type.toLowerCase() === slug
						)
					);
				}

				const vehicleValue = vehicle[facetKey]
					?.toString()
					.toLowerCase();
				return selectedValues.includes(vehicleValue);
			}
		);
	});
};

const matchSatisfiesVehicle = (vehicle, match, matchCondition) => {
	const vehicleModel = vehicle.model?.toLowerCase();
	const target = match?.value?.toLowerCase();
	if (!vehicleModel || !target) return false;
	return matchCondition === 'contains'
		? vehicleModel.includes(target)
		: vehicleModel === target;
};

const vehicleMatchesRule = (vehicle, rule) => {
	return rule.matches.some((match) =>
		matchSatisfiesVehicle(vehicle, match, rule.matchCondition)
	);
};

/**
 * F289003 — Find the first matching rule's `match.trim` for a vehicle within a
 * set of category-scoped rules. Returns null when no rule matches OR when the
 * matching rule's `matches[]` entry has no `trim` field. Used by
 * getShowcaseCategories to bind a category-card-render to the correct trim.
 */
const findActiveCategoryTrimForVehicle = (vehicle, rules) => {
	let matched = null;
	rules.some((rule) => {
		const m = (rule.matches || []).find((match) =>
			matchSatisfiesVehicle(vehicle, match, rule.matchCondition)
		);
		if (m) {
			matched = m;
			return true;
		}
		return false;
	});
	return matched ? matched.trim || null : null;
};

/**
 * Returns true if a vehicle matches at least one mapping rule.
 * Vehicles matching zero rules are "uncategorized" and excluded
 * from showcase mode rendering per BR-SM-003.
 */
export const matchesAnyMappingRule = (vehicle, mappingRules) =>
	mappingRules.some((rule) => vehicleMatchesRule(vehicle, rule));

/**
 * Cascading facet options: for a given facet, returns options derived from the dataset
 * filtered by ALL OTHER active filters (excluding this facet's own filter).
 * This ensures users can never select an option that leads to zero results.
 *
 * @param {object} byVehicle - Vehicles keyed by globalVehicleId
 * @param {object} activeFilters - Current filter selections
 * @param {string} facetKey - The facet to compute options for
 * @returns {Array<{ value: string, count: number }>} Available options with counts
 */
export const getCascadingFacetOptions = (
	byVehicle,
	activeFilters,
	facetKey,
	mappingRules = null
) => {
	if (!byVehicle) return [];

	// BR-SM-003: scope to categorized vehicles when showcase mode active
	let vehicleEntries = Object.values(byVehicle);
	if (mappingRules?.length) {
		vehicleEntries = vehicleEntries.filter((v) =>
			matchesAnyMappingRule(v, mappingRules)
		);
	}

	const otherFilters = Object.entries(activeFilters).filter(
		([key]) => key !== facetKey
	);

	const constrainedVehicles = vehicleEntries.filter((vehicle) => {
		return otherFilters.every(([key, selectedValues]) => {
			if (!selectedValues || selectedValues.length === 0) return true;

			if (key === 'offerTypes') {
				return selectedValues.some((slug) =>
					vehicle.offerTypes.some(
						(type) => type.toLowerCase() === slug
					)
				);
			}

			const vehicleValue = vehicle[key]?.toString().toLowerCase();
			return selectedValues.includes(vehicleValue);
		});
	});

	const optionCounts = {};

	if (facetKey === 'offerTypes') {
		constrainedVehicles.forEach((vehicle) => {
			vehicle.offerTypes.forEach((type) => {
				const slug = type.toLowerCase();
				const count = vehicle.incentives.filter(
					(inc) => inc.incentiveType.toLowerCase() === slug
				).length;
				optionCounts[slug] = (optionCounts[slug] || 0) + count;
			});
		});
	} else {
		constrainedVehicles.forEach((vehicle) => {
			const value = vehicle[facetKey]?.toString().toLowerCase();
			if (value) {
				optionCounts[value] =
					(optionCounts[value] || 0) + vehicle.offerCount;
			}
		});
	}

	return Object.entries(optionCounts)
		.map(([value, count]) => ({ value, count }))
		.sort((a, b) => b.count - a.count);
};

/**
 * BR-SM-003: Get showcase categories with filtered vehicle lists.
 * Vehicles matching zero mapping rules are intentionally excluded — no
 * "Uncategorized" or fallback group is created. Only vehicles that match
 * at least one rule appear in the returned categories.
 *
 * @param {object} incentives - Incentives data from IncentivesContext
 * @param {object} activeFilters - Current filter selections
 * @param {object|null} showcaseMappings - MSS showcase data
 * @returns {Array|null} Filtered categories or null
 */
export const getShowcaseCategories = (
	incentives,
	activeFilters,
	showcaseMappings
) => {
	if (
		!showcaseMappings?.mappingRules?.length ||
		!showcaseMappings?.categories?.length ||
		!incentives?.byVehicle
	)
		return null;

	const filtered = filterVehicles(incentives.byVehicle, activeFilters);

	return showcaseMappings.categories
		.map((category) => {
			const rulesForCategory = showcaseMappings.mappingRules.filter(
				(rule) => rule.tabName === category.tabName
			);
			const vehicles = filtered
				.filter((vehicle) =>
					rulesForCategory.some((rule) =>
						vehicleMatchesRule(vehicle, rule)
					)
				)
				.map((vehicle) => ({
					...vehicle,
					_activeCategoryTrim: findActiveCategoryTrimForVehicle(
						vehicle,
						rulesForCategory
					)
				}));
			return { ...category, vehicles };
		})
		.filter((category) => category.vehicles.length > 0)
		.sort((a, b) => a.displayOrder - b.displayOrder);
};

/**
 * Story 9.3 (F243497): Compute the number of offers on a single vehicle that
 * match the shopper's active INCENTIVE-level filters (currently `offerTypes`).
 *
 * VEHICLE-level filters (year, bodyStyle, make, model) determine WHICH
 * vehicles render in the listing via `filterVehicles` and do NOT affect the
 * per-vehicle incentive count — they are intentionally ignored here.
 *
 * Fast path: when no offerTypes filter is active, returns `vehicle.offerCount`
 * directly so the no-filter case is O(1) — matches today's behavior.
 *
 * @param {object} vehicle - A vehicle record from IncentivesContext.byVehicle.
 * @param {object} activeFilters - Current filter state from FiltersContext.
 * @returns {number} The count of offers on this vehicle matching active filters.
 */
export const computeVehicleOfferCount = (vehicle, activeFilters) => {
	if (!vehicle) return 0;
	const types = activeFilters?.offerTypes;
	if (!types || types.length === 0) return vehicle.offerCount ?? 0;
	// Count per-incentive matches, not per-type. vehicle.offerTypes is a
	// deduplicated list of types the vehicle has, so iterating it would cap
	// the count at the number of distinct matching types (wrong). The
	// authoritative per-incentive collection is vehicle.incentives; each
	// item has an `incentiveType` in raw casing from ice-pack.
	// activeFilters.offerTypes values are already slugs (lowercased).
	if (!vehicle.incentives || vehicle.incentives.length === 0) {
		return vehicle.offerCount ?? 0;
	}
	return vehicle.incentives.filter((inc) =>
		types.includes(inc.incentiveType.toLowerCase())
	).length;
};

/**
 * BR-SM-003: Live filter count for mobile flyout CTA ("Show X Incentives").
 * When showcase mappingRules are present, count is scoped to categorized
 * vehicles only — uncategorized vehicles are excluded from the total.
 *
 * @param {object} incentives - Incentives data from IncentivesContext
 * @param {object} activeFilters - Current filter selections
 * @param {object|null} showcaseMappings - MSS showcase data
 * @returns {number} Count of matching vehicles
 */
export const getLiveFilterCount = (
	incentives,
	activeFilters,
	showcaseMappings
) => {
	const filtered = filterVehicles(incentives?.byVehicle, activeFilters);
	if (showcaseMappings?.mappingRules?.length) {
		return filtered.filter((v) =>
			matchesAnyMappingRule(v, showcaseMappings.mappingRules)
		).length;
	}
	return filtered.length;
};
